import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  AddressProducts,
  ClearingAccount,
  GetAllUnpaidInvoicesResponse,
  GetRatenPlanResponse,
  Stundung,
  UnpaidInvoices,
  VerrechnungskontoResponse,
} from '@mwe/models';
import { catchError, map } from 'rxjs/operators';
import { ENVIRONMENT_SERVICE_TOKEN, FeatureToggleEnum } from '@mwe/constants';
import { lastValueFrom, of } from 'rxjs';
import {
  containsEmobilityOrInternet,
  filterDuePayments,
  getAllProductsFromAddressProducts,
  getCategoriesForClearingAccount,
  getClearingAccountsFrom,
  hasDuePayments,
} from '@mwe/utils';
import { EnvironmentService } from '../../environment/environment.service';
import { ProfileService } from '../../profile/profile.service';

@Injectable({ providedIn: 'root' })
export class ClearingAccountService {
  private readonly httpClient = inject(HttpClient);
  private readonly environmentService = inject<EnvironmentService>(ENVIRONMENT_SERVICE_TOKEN);
  private readonly profileService = inject(ProfileService);

  private readonly resourceUrl: string;

  constructor() {
    this.resourceUrl = this.environmentService.getApiUrl() + 'api/verrechnungskonten/';
  }

  getUnpaidInvoices(accountId: string, businessPartnerId: string, systemId: string): Promise<UnpaidInvoices> {
    if (this.profileService.isFeatureToggleDisabled(FeatureToggleEnum.DUE_PAYMENT_NOTIFICATIONS_ENABLED)) {
      return Promise.resolve(null);
    }

    const url = this.resourceUrl + 'offeneZahlungen';
    const params = new HttpParams()
      .set('verrechnungskontoId', accountId)
      .set('geschaeftsPartnerId', businessPartnerId)
      .set('systemId', systemId);

    return lastValueFrom(
      this.httpClient.get<UnpaidInvoices>(url, { params }).pipe(
        map(response => {
          if (!hasDuePayments(response)) {
            return {
              ...response,
              offeneZahlungen: [],
              teilbetragsInformationen: response?.teilbetragsInformationen ?? [],
            };
          }

          return {
            ...response,
            offeneZahlungen: filterDuePayments(response.offeneZahlungen),
            teilbetragsInformationen: response?.teilbetragsInformationen ?? [],
          };
        }),
        // ignore error and display clearingAccount if there are no unpaid invoices
        catchError(() => of(null)),
      ),
    );
  }

  getAllUnpaidInvoices(addressProducts: AddressProducts[]): Promise<ClearingAccount[]> {
    if (this.profileService.isFeatureToggleDisabled(FeatureToggleEnum.DUE_PAYMENT_NOTIFICATIONS_ENABLED)) {
      return Promise.resolve([]);
    }

    const body: GetAllUnpaidInvoicesResponse = {
      clearingAccounts: getClearingAccountsFrom(addressProducts),
    };

    return lastValueFrom(
      this.httpClient.post<GetAllUnpaidInvoicesResponse>(this.resourceUrl + 'offeneZahlungen', body).pipe(
        map(response => {
          const allProducts = getAllProductsFromAddressProducts(addressProducts);
          return response.clearingAccounts.map(account => {
            if (!account?.unpaidInvoices) {
              return account;
            }

            const categories = getCategoriesForClearingAccount(account, allProducts);
            if (containsEmobilityOrInternet(categories)) {
              return { ...account, unpaidInvoices: null };
            }

            const filteredOffeneZahlungen = hasDuePayments(account.unpaidInvoices)
              ? filterDuePayments(account.unpaidInvoices?.offeneZahlungen)
              : [];

            return {
              ...account,
              unpaidInvoices: {
                ...account.unpaidInvoices,
                offeneZahlungen: filteredOffeneZahlungen,
                teilbetragsInformationen: account.unpaidInvoices?.teilbetragsInformationen ?? [],
              },
            };
          });
        }),
        // ignore error and display clearingAccount if there are no unpaid invoices
        catchError(() => of([])),
      ),
    );
  }

  getPaymentExtension(accountId: string, businessPartnerId: string, systemId: string): Promise<Stundung> {
    const url = this.resourceUrl + 'paymentextension';
    const params = new HttpParams()
      .set('verrechnungskontoId', accountId)
      .set('geschaeftsPartnerId', businessPartnerId)
      .set('systemId', systemId);
    return lastValueFrom(this.httpClient.get<Stundung>(url, { params }));
  }

  getPaymentPlan(accountId: string, businessPartnerId: string, systemId: string): Promise<GetRatenPlanResponse> {
    const url = this.resourceUrl + 'paymentplan';
    const params = new HttpParams()
      .set('verrechnungskontoId', accountId)
      .set('geschaeftsPartnerId', businessPartnerId)
      .set('systemId', systemId);
    return lastValueFrom(this.httpClient.get<GetRatenPlanResponse>(url, { params }));
  }

  getPaymentPlans(accountId: string, businessPartnerId: string, systemId: string): Promise<GetRatenPlanResponse> {
    const url = this.resourceUrl + 'paymentplans';
    const params = new HttpParams()
      .set('verrechnungskontoId', accountId)
      .set('geschaeftsPartnerId', businessPartnerId)
      .set('systemId', systemId);
    return lastValueFrom(this.httpClient.get<GetRatenPlanResponse>(url, { params }));
  }

  getAccountInfo(accountId: string, systemId: string): Promise<VerrechnungskontoResponse> {
    const url = this.resourceUrl + 'accountInfo';
    const params = new HttpParams().set('verrechnungskontoId', accountId).set('systemId', systemId);
    return lastValueFrom(this.httpClient.get<VerrechnungskontoResponse>(url, { params }));
  }
}
